import React, { useEffect, useRef } from "react";
import ReactEcharts from "echarts-for-react";
import { useSelector } from "react-redux";

import { inverseTransform, kPaToPF, logTransform } from "./Gauge2";

import {
  colors,
  colorsSoilMoisture,
  colorsSoilWaterTension,
  colorsPulse,
  colorsDendrometer,
} from "@helper/colors";
import { getMoistureContentCapEnabled } from "@helper/moistureContentDisplay";
import { Button } from "react-bootstrap";

// import InfoBox from "@components/InfoBox/InfoBox";

export default function MoistureGauge({
  tree,
  sensor,
  t,
  soilWaterTensionType,
  setSoilWaterTensionType,
  layoutKey,
  layoutWidth,
  calibrationSpeciesName,
}) {
  const gaugeContainerRef = useRef(null);
  const gaugeChartRef = useRef(null);
  const authUser = useSelector((state) => state.auth?.user);
  const moistureContentCapEnabled = getMoistureContentCapEnabled(authUser);
  // const { t } = useTranslation();
  let COLORS = colors;
  let value = 0;
  let sensorType = "pulse";

  if (sensor) {
    sensorType = sensor.pulse_sensor
      ? "pulse"
      : sensor.soil_moisture_sensor
      ? "soil_moisture"
      : sensor.soil_water_tension_sensor
      ? "soil_water_tension"
      : "dendrometer";
    COLORS =
      sensorType == "soil_moisture"
        ? colorsSoilMoisture(
            tree.soil ? tree.soil.pwp : 10,
            tree.soil ? tree.soil.max_nf : 25,
            tree.soil ? tree.soil.max_f : 45
          )
        : sensorType == "soil_water_tension"
        ? colorsSoilWaterTension(tree.soil)
        : sensorType == "pulse"
        ? colorsPulse(tree.blue_threshold, tree.green_threshold, tree.yellow_threshold)
        : sensorType == "dendrometer"
        ? colorsDendrometer()
        : colors;
    // var color_values = COLORS.map((values) => {
    //   return kPaToPF(values[0]);
    // });
    if (sensorType == "soil_water_tension" && soilWaterTensionType == "pF") {
      COLORS = COLORS.map((values) => {
        return [values[0] == 0 ? 1 : 1 - kPaToPF(values[0]) / kPaToPF(-1585), values[1]];
      });
    }
    if (sensorType == "soil_water_tension" && soilWaterTensionType == "kPa") {
      COLORS = COLORS.map((values) => {
        return [logTransform(values[0], -2000, 0), values[1]];
      });
    }
    const rawValue =
      sensorType == "pulse"
        ? sensor.pulse_sensor.moisture_content
        : sensorType == "soil_moisture"
        ? sensor.soil_moisture_sensor.soil_moisture
        : sensorType == "soil_water_tension"
        ? sensor.soil_water_tension_sensor.soil_water_tension
        : sensorType == "dendrometer"
        ? sensor.dendrometer_sensor.drought_days
        : 0;
    // Apply capping for pulse sensor moisture content if user setting enabled
    value = sensorType == "pulse" && moistureContentCapEnabled ? Math.min(100, rawValue) : rawValue;
  } else {
    // Apply capping for tree moisture content (when no sensor) if user setting enabled
    value = moistureContentCapEnabled
      ? Math.min(100, tree.moisture_content)
      : tree.moisture_content;
  }

  // check for valid points
  if (sensorType == "soil_water_tension") {
    if (value == 255) {
      return (
        <h3>
          {t("unknown")} {t("status")}
        </h3>
      );
    }
  }
  const valueType = sensor
    ? sensorType == "pulse"
      ? "moisture_content"
      : sensorType == "soil_moisture"
      ? "soil_moisture"
      : sensorType == "soil_water_tension"
      ? "soil_water_tension"
      : sensorType == "dendrometer"
      ? "drought_days"
      : ""
    : "moisture_content";
  let valueUnit = "%";
  let max = 100;
  let min = 0;

  if (sensorType == "dendrometer") {
    min = 0;
    max = 14;
    valueUnit = "days";
  } else if (soilWaterTensionType == "kPa") {
    min = sensorType == "soil_water_tension" ? 0 : 0;
    max = sensorType == "soil_water_tension" ? 1 : 100;
    valueUnit = sensorType == "soil_water_tension" ? "kPa" : "%";
  } else {
    min = sensorType == "soil_water_tension" ? 4.2 : 0;
    max = sensorType == "soil_water_tension" ? 0 : 100;
    valueUnit = sensorType == "soil_water_tension" ? "pF" : "%";
  }
  // const title = `${t(valueType)} in ${valueUnit} ${InfoBox({ infoContent: "" })}`;
  const title =
    sensorType === "dendrometer" ? `${t(valueType)}` : `${t(valueType)} in ${valueUnit}`; // ${InfoBox({ infoContent: "" })}`;
  // cone = "HELLO";
  const option = {
    title: {
      text: title,
      // top: "bottom",
      // top: "-2%",
      // padding: 50,
      show: true,
      x: "center",
      // y: "bottom",
      y: "top",
    },
    series: [
      {
        type: "gauge",
        min: min,
        max: max,
        splitNumber: sensorType == "dendrometer" ? 14 : undefined,
        radius: "80%",
        center: ["50%", "50%"],
        axisLine: {
          lineStyle: {
            width: 20,
            color: COLORS,
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          // show: false,
          formatter: function (value) {
            // Convert back to the original value for display
            let realValue = value;
            if (sensorType == "dendrometer") {
              realValue = Math.round(value);
            }
            if (sensorType == "soil_water_tension") {
              if (soilWaterTensionType == "kPa") {
                realValue = inverseTransform(value, -2000, 0).toFixed(0);
              } else {
                Math.max(kPaToPF(value).toFixed(1), 0);
              }
              // realValue = Math.log10(-value).toFixed(0);
              // realValue = kPaToPF(value);
              // realValue = pFToKPa(value);
              // console.log("AXIS", realValue, value);
            }
            // const realValue = Math.pow(max - min + 1, value) + min - 1;
            // return `${realValue} ${valueUnit}`;
            // console.log("AXIS", realValue, value);
            return `${realValue}`;
          },
        },
        pointer: {
          length: 100,
        },
        detail: {
          show: true,
          formatter: function (value) {
            // Convert back to the original value for display
            let realValue = value;
            if (sensorType == "soil_water_tension") {
              if (soilWaterTensionType == "kPa") {
                realValue = inverseTransform(value, -2000, 0).toFixed(0);
              } else {
                Math.max(kPaToPF(value).toFixed(1), 0);
              }
            }
            // const realValue = Math.pow(max - min + 1, value) + min - 1;
            // return `${realValue} ${valueUnit}`;
            return realValue;
          },
        },
        data: [
          {
            value:
              soilWaterTensionType == "kPa"
                ? sensorType == "soil_water_tension"
                  ? logTransform(value, -2000, 0)
                  : value
                : sensorType == "soil_water_tension"
                ? Math.max(kPaToPF(value).toFixed(1), 0)
                : sensorType == "dendrometer"
                ? Math.max(0, Math.min(14, Number(value)))
                : value.toFixed(0),
            // name: `${t(valueType)}`,
          },
        ],
      },
    ],
  };
  const style = {
    height: "280px",
    width: "100%",
  };
  const handleClick = () => {
    if (soilWaterTensionType != "pF") {
      setSoilWaterTensionType("pF");
    } else {
      setSoilWaterTensionType("kPa");
    }
  };

  useEffect(() => {
    const resizeChart = () => {
      const chart = gaugeChartRef.current?.getEchartsInstance?.();
      chart?.resize?.();
    };

    requestAnimationFrame(resizeChart);
    const timer = setTimeout(resizeChart, 150);
    window.addEventListener("resize", resizeChart);

    if (typeof ResizeObserver === "undefined" || !gaugeContainerRef.current) {
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", resizeChart);
      };
    }

    const observer = new ResizeObserver(() => resizeChart());
    observer.observe(gaugeContainerRef.current);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", resizeChart);
      observer.disconnect();
    };
  }, [sensor?.id, soilWaterTensionType, layoutKey, layoutWidth]);

  return (
    <div ref={gaugeContainerRef} style={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      {sensorType == "soil_water_tension" && (
        <Button onClick={handleClick}>{soilWaterTensionType == "kPa" ? "pF" : "kPa"}</Button>
      )}
      {/* {InfoBox({ infoContent: "ABC", infoTitle: "ABCD" })} */}
      <ReactEcharts ref={gaugeChartRef} option={option} style={style} notMerge />
      {sensorType === "pulse" && calibrationSpeciesName && (
        <div
          style={{
            textAlign: "center",
            fontSize: "11px",
            color: "#6c757d",
            marginTop: "-8px",
            fontStyle: "italic",
          }}
          title={t("calibration_species_tooltip")}
        >
          {t("calibration_species")}: <em>{calibrationSpeciesName}</em>
        </div>
      )}
    </div>
  );
}

export function Gauge({ value, min, max, COLORS }) {
  const option = {
    series: [
      {
        type: "gauge",
        // startAngle: 180,
        // endAngle: 0,
        min: min,
        max: max,
        radius: "90%",
        center: ["50%", "50%"],
        axisLine: {
          lineStyle: {
            width: 20,
            color: COLORS,
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        pointer: {
          length: 50,
        },
        detail: {
          show: true,
        },
        data: [
          {
            value: value,
          },
        ],
      },
    ],
  };
  return <ReactEcharts option={option} style={{ height: "120%", width: "120%" }} notMerge />;
}
